// Proposals list view (Overview).
//
// Tabs: Overview, In progress (Draft), Published, Templates, Archived.
// Rows: number, name, client, status badge, created, kebab menu.
// Kebab actions: Duplicate, Duplicate as template, Publish/Unpublish, Archive, Delete.
//
// Session-2 will layer richer filters + the builder itself. For now this is a
// clean, working list on top of the shipped backend.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, MoreHorizontal, FileText, Copy, Layers, Archive, Trash2, Eye, EyeOff, ExternalLink, CheckCircle2, XCircle, LayoutTemplate, UploadCloud, FileSignature, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import PragmaSelect from "../components/PragmaSelect";
import { useConfirm } from "../components/ConfirmModal";

const TABS = [
  { key: "overview",   label: "Overview" },
  { key: "in_progress", label: "In progress" },
  { key: "published",  label: "Published" },
  { key: "templates",  label: "Templates" },
  { key: "archived",   label: "Archived" },
];

const STATUS_META = {
  draft:     { label: "Draft",     fg: "#64748b", bg: "rgba(100,116,139,0.14)" },
  published: { label: "Published", fg: "#5B4FE8", bg: "rgba(91,79,232,0.14)" },
  accepted:  { label: "Accepted",  fg: "#16a34a", bg: "rgba(22,163,74,0.14)" },
  rejected:  { label: "Rejected",  fg: "#dc2626", bg: "rgba(220,38,38,0.14)" },
  archived:  { label: "Archived",  fg: "#94a3b8", bg: "rgba(148,163,184,0.14)" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export default function Proposals() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [search, setSearch] = useState("");
  const [openMenu, setOpenMenu] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/proposals");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load proposals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Close kebab on outside click
  useEffect(() => {
    const h = () => setOpenMenu(null);
    if (openMenu) {
      document.addEventListener("click", h);
      return () => document.removeEventListener("click", h);
    }
  }, [openMenu]);

  const filtered = useMemo(() => {
    let list = rows;
    if (tab === "in_progress") list = list.filter((r) => r.status === "draft" && !r.template);
    else if (tab === "published") list = list.filter((r) => ["published", "accepted", "rejected"].includes(r.status) && !r.template);
    else if (tab === "templates") list = list.filter((r) => r.template);
    else if (tab === "archived")  list = list.filter((r) => r.status === "archived");
    else                          list = list.filter((r) => !r.template && r.status !== "archived");

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((r) =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.number || "").toLowerCase().includes(q) ||
        (r.deal_name || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [rows, tab, search]);

  const kebabAction = async (proposal, action) => {
    setOpenMenu(null);
    try {
      if (action === "duplicate") {
        const { data } = await api.post(`/proposals/${proposal.id}/duplicate`);
        // For a template duplicate, the copy becomes a regular draft — jump the user
        // straight in to fill in the client + pricing.
        toast.success(proposal.template ? "Draft created from template" : "Proposal duplicated");
        navigate(data.kind === "upload" ? `/proposals/${data.id}/sign-builder` : `/proposals/${data.id}`);
      } else if (action === "duplicate_template") {
        await api.post(`/proposals/${proposal.id}/duplicate-as-template`);
        toast.success("Template saved");
        load();
      } else if (action === "publish") {
        await api.post(`/proposals/${proposal.id}/publish`);
        toast.success("Proposal published");
        load();
      } else if (action === "unpublish") {
        await api.post(`/proposals/${proposal.id}/unpublish`);
        toast.success("Moved back to Draft");
        load();
      } else if (action === "archive") {
        const ok = await confirm({
          title: "Archive this proposal?",
          message: "It'll be hidden from the active list but kept for records. You can find it under the Archived tab.",
          confirmLabel: "Archive",
        });
        if (!ok) return;
        await api.post(`/proposals/${proposal.id}/archive`);
        toast.success("Archived");
        load();
      } else if (action === "delete") {
        const ok = await confirm({
          title: "Delete this proposal?",
          message: `Proposal ${proposal.number} will be deleted permanently. This cannot be undone.`,
          confirmLabel: "Delete",
          destructive: true,
        });
        if (!ok) return;
        await api.delete(`/proposals/${proposal.id}`);
        toast.success("Deleted");
        load();
      } else if (action === "convert") {
        await api.post(`/proposals/${proposal.id}/convert`);
        toast.success("Proposal converted — Deal marked Won");
        load();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong");
    }
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="proposals-page">
      <PageHeader title="Proposals" subtitle={`${filtered.length} of ${rows.filter(r => !r.template).length} proposals`}>
        <button
          onClick={() => setShowCreate(true)}
          className="pg-btn pg-btn-primary"
          data-testid="proposals-new-btn"
        >
          <Plus size={14} /> New proposal
        </button>
      </PageHeader>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4 overflow-x-auto" style={{ borderBottom: "1px solid var(--pg-border)" }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`proposals-tab-${t.key}`}
            className="whitespace-nowrap"
            style={{
              padding: "10px 14px",
              fontSize: 13,
              fontWeight: tab === t.key ? 600 : 400,
              color: tab === t.key ? "var(--pg-accent)" : "var(--pg-text-mute)",
              borderBottom: `2px solid ${tab === t.key ? "var(--pg-accent)" : "transparent"}`,
              marginBottom: -1,
              cursor: "pointer",
              background: "transparent",
              transition: "color 120ms",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search proposal name, number, or deal…"
          data-testid="proposals-search"
          className="pg-input"
          style={{ width: 340, maxWidth: "100%" }}
        />
      </div>

      {/* List */}
      <div className="pg-card overflow-visible">
        {loading ? (
          <div className="p-10 text-sm text-center" style={{ color: "var(--pg-text-mute)" }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState tab={tab} onCreate={() => setShowCreate(true)} />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--pg-border)", color: "var(--pg-text-mute)" }}>
                <th className="text-left p-3 font-medium">Number</th>
                <th className="text-left p-3 font-medium">Proposal</th>
                <th className="text-left p-3 font-medium">Deal</th>
                <th className="text-left p-3 font-medium">Status</th>
                <th className="text-left p-3 font-medium">Created</th>
                <th className="text-right p-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const meta = STATUS_META[p.status] || STATUS_META.draft;
                return (
                  <tr
                    key={p.id}
                    style={{ borderBottom: "1px solid var(--pg-border)", cursor: "pointer" }}
                    onClick={() => navigate(p.kind === "upload" ? `/proposals/${p.id}/sign-builder` : `/proposals/${p.id}`)}
                    data-testid={`proposals-row-${p.id}`}
                    className="hover:bg-black/[.02]"
                  >
                    <td className="p-3 mono" style={{ fontSize: 12, color: "var(--pg-text-mute)" }}>{p.number}</td>
                    <td className="p-3" style={{ fontWeight: 500 }}>{p.name}</td>
                    <td className="p-3" style={{ color: "var(--pg-text-mute)" }}>{p.deal_name || "—"}</td>
                    <td className="p-3">
                      <span style={{
                        display: "inline-block", padding: "2px 10px", borderRadius: 100,
                        fontSize: 11, fontWeight: 500, color: meta.fg, background: meta.bg,
                      }}>{p.template ? "Template" : meta.label}</span>
                    </td>
                    <td className="p-3" style={{ color: "var(--pg-text-mute)" }}>{fmtDate(p.created_at)}</td>
                    <td className="p-3 text-right relative" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setOpenMenu(openMenu === p.id ? null : p.id)}
                        className="pg-btn pg-btn-ghost"
                        style={{ padding: 6 }}
                        data-testid={`proposals-kebab-${p.id}`}
                      >
                        <MoreHorizontal size={16} />
                      </button>
                      {openMenu === p.id && (
                        <KebabMenu
                          proposal={p}
                          onAction={(a) => kebabAction(p, a)}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <NewProposalModal
          onClose={() => setShowCreate(false)}
          onCreated={(p, kind) => {
            setShowCreate(false);
            navigate(kind === "upload" ? `/proposals/${p.id}/sign-builder` : `/proposals/${p.id}`);
          }}
        />
      )}
    </div>
  );
}

function KebabMenu({ proposal, onAction }) {
  const isDraft     = proposal.status === "draft" && !proposal.template;
  const isPublished = proposal.status === "published";
  const isAccepted  = proposal.status === "accepted";
  const isArchived  = proposal.status === "archived";
  const isTemplate  = proposal.template;
  return (
    <div
      className="absolute right-3 top-9 z-50"
      style={{
        background: "var(--pg-surface)",
        border: "1px solid var(--pg-border)",
        borderRadius: 8,
        boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
        minWidth: 210,
        padding: 4,
        animation: "invModalRise 140ms ease-out",
      }}
      data-testid={`proposals-kebab-menu-${proposal.id}`}
    >
      {isDraft && (
        <MenuItem icon={<Eye size={13} />} label="Publish" onClick={() => onAction("publish")} testid={`kebab-publish-${proposal.id}`} />
      )}
      {isPublished && (
        <MenuItem icon={<EyeOff size={13} />} label="Unpublish" onClick={() => onAction("unpublish")} testid={`kebab-unpublish-${proposal.id}`} />
      )}
      {isAccepted && (
        <MenuItem icon={<CheckCircle2 size={13} />} label="Convert (mark Deal Won)" onClick={() => onAction("convert")} testid={`kebab-convert-${proposal.id}`} />
      )}
      <MenuItem
        icon={<Copy size={13} />}
        label={proposal.template ? "Use this template" : "Duplicate"}
        onClick={() => onAction("duplicate")}
        testid={`kebab-duplicate-${proposal.id}`}
      />
      {!isTemplate && (
        <MenuItem icon={<Layers size={13} />} label="Duplicate as template" onClick={() => onAction("duplicate_template")} testid={`kebab-dup-template-${proposal.id}`} />
      )}
      {!isArchived && (
        <MenuItem icon={<Archive size={13} />} label="Archive" onClick={() => onAction("archive")} testid={`kebab-archive-${proposal.id}`} />
      )}
      <MenuItem icon={<Trash2 size={13} />} label="Delete" onClick={() => onAction("delete")} destructive testid={`kebab-delete-${proposal.id}`} />
    </div>
  );
}

function MenuItem({ icon, label, onClick, destructive, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="w-full text-left flex items-center gap-2"
      style={{
        padding: "8px 10px", fontSize: 13, borderRadius: 6,
        color: destructive ? "#dc2626" : "var(--pg-text)",
        background: "transparent", cursor: "pointer",
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = "rgba(91,79,232,0.06)"}
      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function EmptyState({ tab, onCreate }) {
  const messages = {
    overview: "You haven't created any proposals yet.",
    in_progress: "Nothing in progress right now.",
    published: "You haven't published anything yet.",
    templates: "No saved templates. Save any proposal as a template to reuse it.",
    archived: "Nothing archived.",
  };
  return (
    <div className="py-16 flex flex-col items-center text-center" data-testid="proposals-empty">
      <div
        style={{
          width: 56, height: 56, borderRadius: 14,
          background: "rgba(91,79,232,0.08)", color: "var(--pg-accent)",
          display: "flex", alignItems: "center", justifyContent: "center",
          marginBottom: 14,
        }}
      >
        <FileText size={26} />
      </div>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{messages[tab]}</div>
      <div className="text-xs mb-5" style={{ color: "var(--pg-text-mute)", maxWidth: 320 }}>
        Proposals are always tied to a Deal — pick a Deal and Pragma pre-fills the client and any deliverables you've already lined up.
      </div>
      {tab !== "archived" && tab !== "templates" && (
        <button onClick={onCreate} className="pg-btn pg-btn-primary" data-testid="proposals-empty-create">
          <Plus size={14} /> Create your first proposal
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New Proposal modal — step 1 pick a creation method, step 2 pick a deal
// (and optionally upload a PDF for the "upload" flow).
// ---------------------------------------------------------------------------
function NewProposalModal({ onClose, onCreated }) {
  // "chooser" → pick method; "builder" | "upload" → sub-flow
  const [step, setStep] = useState("chooser");
  const [deals, setDeals] = useState([]);
  const [dealId, setDealId] = useState("");
  const [prefill, setPrefill] = useState(true);
  const [creating, setCreating] = useState(false);
  // Upload-only state
  const [file, setFile] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null); // {file_id, filename, size, pdf_meta}
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/deals");
        setDeals(Array.isArray(data) ? data : []);
      } catch { /* silent */ }
    })();
  }, []);

  const dealOptions = deals
    .filter(d => d.status !== "won" && d.status !== "lost")
    .map(d => ({ value: d.id, label: d.name || "(unnamed deal)", hint: d.stage?.name }));

  const submitBuilder = async () => {
    if (!dealId) return;
    setCreating(true);
    try {
      const { data } = await api.post("/proposals", { deal_id: dealId, prefill_from_deal: prefill });
      onCreated(data, "builder");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create proposal");
    } finally {
      setCreating(false);
    }
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") {
      toast.error("Only PDF files are supported");
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast.error("PDF too large — max 15 MB");
      return;
    }
    setFile(f);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/proposals/upload-doc", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadedFile(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
      setFile(null);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const submitUpload = async () => {
    if (!dealId || !uploadedFile) return;
    setCreating(true);
    try {
      const { data } = await api.post("/proposals/upload-create", {
        deal_id: dealId,
        file_id: uploadedFile.file_id,
      });
      onCreated(data, "upload");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create proposal");
    } finally {
      setCreating(false);
    }
  };

  const title = step === "chooser"
    ? "How do you want to create this proposal?"
    : step === "builder" ? "New proposal — Builder" : "New proposal — Upload PDF";

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 200, background: "rgba(15,23,42,0.55)", animation: "fadeIn 160ms ease-out" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="proposals-new-modal"
    >
      <div style={{
        width: step === "chooser" ? 620 : 500, maxWidth: "94vw", background: "var(--pg-surface)",
        borderRadius: 14, border: "1px solid var(--pg-border)", padding: 24,
        boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
        animation: "invModalRise 220ms ease-out",
      }}>
        <h3 style={{ fontSize: 17, fontWeight: 600, margin: "0 0 6px" }}>{title}</h3>

        {step === "chooser" && (
          <>
            <p className="text-sm mb-5" style={{ color: "var(--pg-text-mute)" }}>
              Pick the path that fits — a visual builder for proposals you create from scratch, or upload a signed-ready PDF and add client fields on a signing page.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <ChoiceCard
                testId="proposals-new-choice-builder"
                icon={<LayoutTemplate size={22} />}
                title="Use the proposal builder"
                description="Headings, services, pricing tables, images, acceptance signature — fully editable blocks."
                onClick={() => setStep("builder")}
              />
              <ChoiceCard
                testId="proposals-new-choice-upload"
                icon={<UploadCloud size={22} />}
                title="Upload a document"
                description="PDF (portrait or landscape). We append a signing page where you place client fields and signature boxes."
                onClick={() => setStep("upload")}
              />
            </div>
            <div className="flex items-center justify-end gap-2 mt-6">
              <button onClick={onClose} className="pg-btn pg-btn-ghost" data-testid="proposals-new-cancel">Cancel</button>
            </div>
          </>
        )}

        {step === "builder" && (
          <>
            <p className="text-sm mb-5" style={{ color: "var(--pg-text-mute)" }}>
              Every proposal belongs to a Deal. Pragma will pre-fill the client and any deliverables you've already lined up.
            </p>
            <label className="block mb-2 text-xs" style={{ color: "var(--pg-text-mute)" }}>Deal</label>
            <PragmaSelect
              value={dealId}
              onChange={setDealId}
              options={[{ value: "", label: "— Select a deal —" }, ...dealOptions]}
              testId="proposals-new-deal-select"
            />
            <label className="flex items-center gap-2 mt-4 text-sm" style={{ cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={prefill}
                onChange={(e) => setPrefill(e.target.checked)}
                data-testid="proposals-new-prefill"
              />
              <span>Pre-fill Service blocks from the Deal's deliverables</span>
            </label>
            <div className="flex items-center justify-between gap-2 mt-6">
              <button onClick={() => setStep("chooser")} className="pg-btn pg-btn-ghost" data-testid="proposals-new-back">← Back</button>
              <div className="flex gap-2">
                <button onClick={onClose} className="pg-btn pg-btn-ghost">Cancel</button>
                <button
                  onClick={submitBuilder}
                  disabled={!dealId || creating}
                  className="pg-btn pg-btn-primary"
                  data-testid="proposals-new-submit"
                >
                  {creating ? "Creating…" : "Create draft"}
                </button>
              </div>
            </div>
          </>
        )}

        {step === "upload" && (
          <>
            <p className="text-sm mb-5" style={{ color: "var(--pg-text-mute)" }}>
              Upload the proposal PDF — we'll add a signing page where the client fills their details and signs. Portrait or landscape, up to 15 MB.
            </p>
            <label className="block mb-2 text-xs" style={{ color: "var(--pg-text-mute)" }}>Deal</label>
            <PragmaSelect
              value={dealId}
              onChange={setDealId}
              options={[{ value: "", label: "— Select a deal —" }, ...dealOptions]}
              testId="proposals-upload-deal-select"
            />

            <label className="block mb-2 mt-4 text-xs" style={{ color: "var(--pg-text-mute)" }}>Document</label>
            {!uploadedFile ? (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="proposals-upload-pick-file"
                className="w-full"
                style={{
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
                  padding: "28px 16px", borderRadius: 10,
                  border: "2px dashed var(--pg-border)", background: "var(--pg-surface-2, rgba(0,0,0,0.02))",
                  cursor: "pointer", color: "var(--pg-text-mute)",
                }}
              >
                {uploading ? <Loader2 size={22} className="animate-spin" /> : <UploadCloud size={22} />}
                <div style={{ fontSize: 13, fontWeight: 500, color: "var(--pg-text)" }}>
                  {uploading ? "Uploading…" : "Click to upload a PDF"}
                </div>
                <div className="text-xs">Portrait or landscape · 15 MB max</div>
              </button>
            ) : (
              <div style={{
                padding: 12, borderRadius: 10, border: "1px solid var(--pg-border)",
                background: "var(--pg-surface-2)", display: "flex", alignItems: "center", gap: 10,
              }} data-testid="proposals-upload-filecard">
                <FileText size={18} style={{ color: "var(--pg-purple)" }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="text-sm truncate" style={{ fontWeight: 500 }}>{uploadedFile.filename}</div>
                  <div className="mono text-[11px]" style={{ color: "var(--pg-text-mute)" }}>
                    {uploadedFile.pdf_meta?.page_count} page{uploadedFile.pdf_meta?.page_count === 1 ? "" : "s"}
                    {" · "}{uploadedFile.pdf_meta?.orientation}
                    {" · "}{(uploadedFile.size / 1024).toFixed(0)} KB
                  </div>
                </div>
                <button
                  type="button"
                  className="pg-btn pg-btn-ghost"
                  onClick={() => { setUploadedFile(null); setFile(null); }}
                  data-testid="proposals-upload-replace"
                  style={{ padding: 6 }}
                  aria-label="Replace file"
                ><Trash2 size={14} /></button>
              </div>
            )}
            <input ref={fileInputRef} type="file" accept="application/pdf" onChange={onPickFile} style={{ display: "none" }} data-testid="proposals-upload-file-input" />

            <div className="flex items-center justify-between gap-2 mt-6">
              <button onClick={() => setStep("chooser")} className="pg-btn pg-btn-ghost" data-testid="proposals-upload-back">← Back</button>
              <div className="flex gap-2">
                <button onClick={onClose} className="pg-btn pg-btn-ghost">Cancel</button>
                <button
                  onClick={submitUpload}
                  disabled={!dealId || !uploadedFile || creating}
                  className="pg-btn pg-btn-primary"
                  data-testid="proposals-upload-submit"
                >
                  {creating ? "Creating…" : (<><FileSignature size={13} /> Build signing page</>)}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ChoiceCard({ icon, title, description, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="text-left"
      style={{
        padding: 16, borderRadius: 12,
        border: "1px solid var(--pg-border)",
        background: "var(--pg-surface)", cursor: "pointer",
        display: "flex", flexDirection: "column", gap: 8,
        transition: "all 160ms",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "var(--pg-accent)";
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = "0 6px 18px rgba(91,79,232,0.14)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--pg-border)";
        e.currentTarget.style.transform = "none";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        background: "rgba(91,79,232,0.10)", color: "var(--pg-accent)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>{icon}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--pg-text)" }}>{title}</div>
      <div className="text-xs" style={{ color: "var(--pg-text-mute)", lineHeight: 1.45 }}>{description}</div>
    </button>
  );
}
